import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  FiArrowRight,
  FiShield,
  FiAward,
  FiUsers,
  FiCalendar,
  FiX,
} from "react-icons/fi";
import { createAppointment, clearError } from "../redux/appointmentSlice";

export const Header = (props) => {
  const title =
    props?.data?.title ||
    "Mardin Artuklu | Dilan Aslan Çocuk Gelişim Danışmanlığı";
  const seo = props?.data?.seo;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.appointments);

  // Telefon numarası kontrol ve format fonksiyonu
  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");

    if (value.startsWith("0")) {
      value = value.substring(1);
    }

    if (value.length > 10) {
      value = value.substring(0, 10);
    }

    setPhone(value);

    if (value.length > 0 && value.length < 10) {
      setPhoneError("Telefon numarası 10 haneli olmalıdır");
    } else if (value.length === 10 && !value.startsWith("5")) {
      setPhoneError("Telefon numarası 5 ile başlamalıdır");
    } else {
      setPhoneError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (phone.length !== 10) {
      alert("❌ Telefon numarası 10 haneli olmalıdır");
      return;
    }

    if (!phone.startsWith("5")) {
      alert("❌ Telefon numarası 5 ile başlamalıdır (örn: 5340834440)");
      return;
    }

    try {
      await dispatch(
        createAppointment({
          fullName: name,
          phone: "0" + phone,
          date,
        })
      ).unwrap();

      alert(
        "✅ Randevu talebiniz alındı! Randevu saatiniz en kısa sürede size bildirilecektir."
      );
      setName("");
      setPhone("");
      setDate("");
      setPhoneError("");
      setShowModal(false);
      dispatch(clearError());
    } catch (err) {
      alert("❌ Randevu oluşturulamadı. Tekrar deneyin.");
    }
  };

  const scrollToServices = () => {
    document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
  };

  // Schema.org JSON-LD
  const schemaOrgJSONLD = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Dilan Aslan | Çocuk Gelişim Danışmanlığı",
    description:
      "Mardin Artuklu'da uzman çocuk gelişim danışmanlığı. Oyun terapisi, ebeveyn danışmanlığı, gelişim değerlendirmesi ve bireysel terapi hizmetleri.",
    image: "https://www.dilanaslan.com/img/intro-bg11.jpg",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Artuklu",
      addressLocality: "Mardin",
      addressRegion: "Mardin",
      postalCode: "47200",
      addressCountry: "TR",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "37.3212",
      longitude: "40.7245",
    },
    telephone: "+905055517747",
    url: "https://www.dilanaslan.com",
    priceRange: "₺₺",
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "18:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "10:00",
        closes: "15:00",
      },
    ],
    areaServed: {
      "@type": "City",
      name: "Mardin",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Çocuk Gelişim Hizmetleri",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Çocuk Gelişim Danışmanlığı",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Oyun Terapisi",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Ebeveyn Danışmanlığı",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Gelişim Değerlendirmesi",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Bireysel Terapi",
          },
        },
      ],
    },
    sameAs: ["https://www.instagram.com/cg.dilanaslan/"],
  };

  return (
    <>
      <Helmet>
        {/* Temel Meta Etiketleri */}
        <html lang="tr" />
        <title>
          {seo?.title ||
            "Dilan Aslan | Mardin Artuklu Çocuk Gelişim Danışmanlığı - Oyun Terapisi"}
        </title>
        <meta
          name="description"
          content={
            seo?.description ||
            "Mardin Artuklu'da uzman çocuk gelişim danışmanlığı. Oyun terapisi, ebeveyn danışmanlığı, gelişim değerlendirmesi ve bireysel terapi hizmetleri. Uzman Dilan Aslan ile çocuğunuzun potansiyelini keşfedin."
          }
        />
        <meta
          name="keywords"
          content="Mardin çocuk gelişimi, çocuk gelişim danışmanı Mardin, Artuklu oyun terapisi, ebeveyn danışmanlığı, çocuk gelişim uzmanı, Dilan Aslan, çocuk terapisi Mardin, gelişim değerlendirmesi, çocuk psikolojisi, oyun terapisti Artuklu, çocuk danışmanı, Mardin pedagog, çocuk davranış problemleri"
        />
        <meta name="author" content="Dilan Aslan" />
        <link rel="canonical" href="https://www.dilanaslan.com/" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.dilanaslan.com/" />
        <meta
          property="og:title"
          content="Dilan Aslan | Mardin Artuklu Çocuk Gelişim Danışmanlığı"
        />
        <meta
          property="og:description"
          content="Mardin Artuklu'da uzman çocuk gelişim danışmanlığı. Oyun terapisi, ebeveyn danışmanlığı ve bireysel terapi hizmetleri ile çocuğunuzun gelişimini destekleyin."
        />
        <meta
          property="og:image"
          content="https://www.dilanaslan.com/img/intro-bg11.jpg"
        />
        <meta property="og:locale" content="tr_TR" />
        <meta
          property="og:site_name"
          content="Dilan Aslan Çocuk Gelişim Danışmanlığı"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.dilanaslan.com/" />
        <meta
          name="twitter:title"
          content="Dilan Aslan | Mardin Artuklu Çocuk Gelişim Danışmanlığı"
        />
        <meta
          name="twitter:description"
          content="Mardin Artuklu'da uzman çocuk gelişim danışmanlığı. Oyun terapisi ve ebeveyn danışmanlığı hizmetleri."
        />
        <meta
          name="twitter:image"
          content="https://www.dilanaslan.com/img/intro-bg11.jpg"
        />

        {/* Coğrafi Etiketler */}
        <meta name="geo.region" content="TR-47" />
        <meta name="geo.placename" content="Mardin, Artuklu" />
        <meta name="geo.position" content="37.3212;40.7245" />
        <meta name="ICBM" content="37.3212, 40.7245" />

        {/* Mobil Optimizasyon */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#2563EB" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgJSONLD)}
        </script>

        {/* Robots */}
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <meta name="googlebot" content="index, follow" />
      </Helmet>

      <section
        id="home"
        className="bg-gradient-to-br mt-20 from-blue-50 via-white to-blue-50 min-h-screen flex items-center"
      >
        <div className="w-full max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12 py-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Sol Taraf - İçerik */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block text-xl md:text-2xl bg-blue-100 text-blue-800 px-6 py-3 rounded-full text-base font-semibold mb-8">
                Profesyonel Çocuk Gelişimi Danışmanlığı
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-8">
                Çocuğunuzun Geleceğini
                <span className="text-blue-600"> Birlikte</span> İnşa Edelim
              </h1>
              <p className="text-xl md:text-3xl text-gray-600 mb-10 leading-relaxed">
                Çocuk gelişimini destekleyen, oyun terapisi ve ebeveyn
                danışmanlığı ile çocuğunuzun potansiyelini keşfetmesine yardımcı
                oluyoruz. Mardin Artuklu'da uzman desteği.
              </p>
              <div className="flex flex-col sm:flex-row gap-5">
                <button
                  onClick={() => setShowModal(true)}
                  className="bg-blue-600 text-white px-10 py-5 rounded-xl text-2xl font-semibold hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center"
                >
                  <FiCalendar className="mr-3" size={24} />
                  Randevu Al
                  <FiArrowRight className="ml-3" size={24} />
                </button>
                <button
                  onClick={scrollToServices}
                  className="bg-white text-blue-600 px-10 py-5 rounded-xl text-2xl font-semibold border-2 border-blue-600 hover:bg-blue-50 transition-all"
                >
                  Hizmetlerimiz
                </button>
              </div>
            </motion.div>

            {/* Sağ Taraf - Görsel */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl p-8 shadow-2xl">
                <div className="bg-white rounded-2xl p-4">
                  <img
                    src="/img/intro-bg11.jpg"
                    alt="Çocuk Gelişimi - Dilan Aslan"
                    className="w-full h-96 md:h-[450px] object-cover rounded-xl"
                  />
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <FiAward className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Uzman Danışman</p>
                    <p className="text-xl text-gray-600">Dilan Aslan</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Alt Kartlar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-24">
            <motion.div
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="bg-blue-100 w-16 h-16 rounded-xl flex items-center justify-center mb-5">
                <FiShield className="text-blue-600" size={32} />
              </div>
              <h3 className="text-4xl font-bold text-gray-900 mb-3">
                Güvenilir Hizmet
              </h3>
              <p className="text-gray-600 text-2xl">
                Lisanslı ve sertifikalı uzman desteği ile en yüksek kalite
                standartlarında hizmet sunuyoruz.
              </p>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="bg-blue-100 w-16 h-16 rounded-xl flex items-center justify-center mb-5">
                <FiUsers className="text-blue-600" size={32} />
              </div>
              <h3 className="text-4xl font-bold text-gray-900 mb-3">
                Oyun Terapisi
              </h3>
              <p className="text-gray-600 text-2xl">
                Çocukların doğal dili olan oyun aracılığıyla duygusal ve sosyal
                gelişimlerini destekliyoruz.
              </p>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="bg-blue-100 w-16 h-16 rounded-xl flex items-center justify-center mb-5">
                <FiAward className="text-blue-600" size={32} />
              </div>
              <h3 className="text-4xl font-bold text-gray-900 mb-3">
                Bireysel Yaklaşım
              </h3>
              <p className="text-gray-600 text-2xl">
                Her çocuk özeldir. Kişiye özel programlar ve bireysel destek
                sağlıyoruz.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Randevu Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl relative"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiX size={24} />
            </button>

            <div className="text-center mb-6">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCalendar className="text-blue-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                Randevu Talebi
              </h3>
              <p className="text-gray-600 mt-2">
                Formu doldurun, size en kısa sürede dönüş yapalım.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Ad Soyad
                </label>
                <input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  type="text"
                  placeholder="Adınızı soyadınızı girin"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Telefon
                </label>
                <input
                  id="phone"
                  value={phone}
                  onChange={handlePhoneChange}
                  type="tel"
                  placeholder="5xx xxx xx xx"
                  required
                  maxLength="10"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                />
                {phoneError && (
                  <span className="text-red-500 text-sm mt-1 block">
                    {phoneError}
                  </span>
                )}
              </div>

              <div>
                <label
                  htmlFor="date"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Tarih Seçin
                </label>
                <input
                  id="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  type="date"
                  min={new Date().toISOString().split("T")[0]}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                />
                <p className="text-gray-500 text-xs mt-2">
                  * Randevu saatiniz tarafımızca belirlenip size SMS ile
                  bildirilecektir.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || phoneError !== "" || phone.length !== 10}
                className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    Randevu Talebi Gönder
                    <FiArrowRight className="ml-2" size={20} />
                  </>
                )}
              </button>

              {error && (
                <p className="text-red-500 text-center text-sm">
                  Hata: {error}
                </p>
              )}
            </form>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default Header;
